import React, { useState } from "react";
import Modal from "../Common/Modal";
import ConfirmDialog from "../Common/ConfirmDialog";
import { useLanguage } from "../../context/LanguageContext";

const EquipmentTable = ({
  equipment,
  onEdit,
  onDelete,
  onViewDetails,
  onSaveAsTemplate,
}) => {
  const { t } = useLanguage();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [formData, setFormData] = useState({
    model: "",
    serial_number: "",
    brand: "",
    category: "",
    article_id: "",
  });

  const handleEdit = (item) => {
    setSelectedEquipment(item);
    setFormData({
      model: item.model || "",
      serial_number: item.serial_number || "",
      brand: item.brand || "",
      category: item.category || "",
      article_id: item.article_id || "",
    });
    setShowEditModal(true);
  };

  const handleDelete = (item) => {
    setSelectedEquipment(item);
    setShowDeleteDialog(true);
  };

  console.log("equipment-table", equipment);

  const handleSubmitEdit = (e) => {
    e.preventDefault();
    onEdit(selectedEquipment.id, formData);
    setShowEditModal(false);
    setSelectedEquipment(null);
  };

  const confirmDelete = () => {
    onDelete(selectedEquipment.id);
    setShowDeleteDialog(false);
    setSelectedEquipment(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getCreatedByDisplay = (item) => {
    if (item.created_by_name && item.created_by_name.trim()) {
      return item.created_by_name;
    }
    if (item.created_by_username && item.created_by_username.trim()) {
      return item.created_by_username;
    }
    return t("equipmentTable.unknown");
  };

  const isUnknownCreator = (item) =>
    !(item.created_by_name && item.created_by_name.trim()) &&
    !(item.created_by_username && item.created_by_username.trim());

  const getInitials = (item) => {
    if (isUnknownCreator(item)) return "U";
    return getCreatedByDisplay(item).charAt(0).toUpperCase();
  };

  return (
    <>
      <div className="table-container">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t("equipmentTable.colModel")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t("equipmentTable.colSerialNumber")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t("equipmentTable.colArticleId")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t("equipmentTable.colCategory")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t("equipmentTable.colBrand")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t("equipmentTable.colCreatedBy")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t("equipmentTable.colDateCreated")}
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t("equipmentTable.colActions")}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {equipment.map((item) => (
              <tr key={item.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {item.model}
                  </div>
                  {item.created_from_template && (
                    <div className="text-xs text-gray-400">
                      {t("equipmentTable.fromPrefix", { name: item.created_from_template })}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {item.serial_number || "-"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {item.article_id || "-"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {item.category || "-"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {item.brand || "-"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  <div className="flex items-center">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center mr-2 ${
                        isUnknownCreator(item)
                          ? "bg-gray-100"
                          : "bg-blue-100"
                      }`}
                    >
                      <span
                        className={`font-semibold text-xs ${
                          isUnknownCreator(item)
                            ? "text-gray-400"
                            : "text-blue-600"
                        }`}
                      >
                        {getInitials(item)}
                      </span>
                    </div>
                    <p
                      className={`font-medium ${
                        isUnknownCreator(item)
                          ? "text-gray-400 italic"
                          : ""
                      }`}
                    >
                      {getCreatedByDisplay(item)}
                    </p>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(item.created_at)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                  <button
                    onClick={() => onViewDetails(item)}
                    className="text-purple-600 hover:text-purple-900"
                  >
                    {t("equipmentTable.view")}
                  </button>
                  {onSaveAsTemplate && (
                    <button
                      onClick={() => onSaveAsTemplate(item)}
                      className="text-green-600 hover:text-green-900"
                      title={t("equipmentTable.saveAsTemplate")}
                    >
                      💾
                    </button>
                  )}
                  <button
                    onClick={() => handleEdit(item)}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    {t("equipmentTable.edit")}
                  </button>
                  {onDelete && (
                    <button
                      onClick={() => handleDelete(item)}
                      className="text-red-600 hover:text-red-900"
                    >
                      {t("equipmentTable.delete")}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title={t("equipmentTable.editEquipmentTitle")}
        size="md"
      >
        <form onSubmit={handleSubmitEdit}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("equipmentTable.modelName")} *
                </label>
                <input
                  type="text"
                  value={formData.model}
                  onChange={(e) =>
                    setFormData({ ...formData, model: e.target.value })
                  }
                  className="input-field"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("equipmentTable.serialNumber")}
                </label>
                <input
                  type="text"
                  value={formData.serial_number}
                  onChange={(e) =>
                    setFormData({ ...formData, serial_number: e.target.value })
                  }
                  className="input-field"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("equipmentTable.brand")}
                </label>
                <input
                  type="text"
                  value={formData.brand}
                  onChange={(e) =>
                    setFormData({ ...formData, brand: e.target.value })
                  }
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("equipmentTable.category")}
                </label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                  className="input-field"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("equipmentTable.articleId")}
              </label>
              <input
                type="text"
                value={formData.article_id}
                onChange={(e) =>
                  setFormData({ ...formData, article_id: e.target.value })
                }
                className="input-field"
              />
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => setShowEditModal(false)}
                className="btn-secondary"
              >
                {t("equipmentTable.cancel")}
              </button>
              <button type="submit" className="btn-primary">
                {t("equipmentTable.saveChanges")}
              </button>
            </div>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={confirmDelete}
        title={t("equipmentTable.deleteEquipmentTitle")}
        message={t("equipmentTable.confirmDeleteMessage", { model: selectedEquipment?.model })}
      />
    </>
  );
};

export default EquipmentTable;
